import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {PolarisApiService} from './polaris-api.service';
import {StorageService} from '../storage.service';
import {LocalStorageKeys} from '../../shared/types/storage';
import {getPolarisEnvId} from './polaris-env';
import {toPolarisOrigin} from './polaris.utils';
import {
  PolarisChatAggregate,
  PolarisChatId,
  PolarisEnvId,
  PolarisGraph,
  PolarisGraphSeries,
  PolarisGraphType,
  PolarisLegacyChat,
  PolarisLegacyChatsMap,
  PolarisLegacyChatMessage,
  PolarisMessagePair,
  PolarisMigrationAttemptFailureSummary,
  PolarisMigrationAttemptSuccessSummary,
  PolarisMigrationErrorCategory,
  PolarisMigrationOrchestratorContract,
  PolarisMigrationScope,
  PolarisMigrationSuccessMarker,
  PolarisMigrationUploadError,
  PolarisRuntimeInitResult,
  PolarisRuntimeModeSelection,
  PolarisTurn,
  PolarisTurnStatus
} from './model';
import type {graphSpec} from '../../components/copilot/types';

@Injectable({
  providedIn: 'root'
})
export class PolarisMigrationService implements PolarisMigrationOrchestratorContract {
  private readonly maxUploadAttempts = 2;
  private readonly retryBaseDelayMs = 300;
  private polarisApiService = inject(PolarisApiService);
  private storageService = inject(StorageService);

  public async initializeRuntime(scope: PolarisMigrationScope): Promise<PolarisRuntimeInitResult> {
    const marker = this.getSuccessMarker(scope);
    if (marker) {
      return {
        modeSelection: this.buildModeSelection('new', 'migration_marker')
      };
    }

    try {
      await firstValueFrom(this.polarisApiService.getChatsOverview(scope.accountKey, {limit: 1}));
    } catch (error) {
      this.setAttemptStamp(scope);
      const category = this.classifyErrorCategory(error);
      const statusCode = this.extractStatusCode(error);
      const message = this.errorMessage(error);
      const migrationAttempt: PolarisMigrationAttemptFailureSummary = {
        status: 'failed',
        totalChatCount: 0,
        uploadedChatCount: 0,
        attemptCount: 1,
        errorCategory: category,
        statusCode,
        retryExhausted: true,
        errorMessage: message
      };
      return {
        modeSelection: this.buildModeSelection('legacy', 'migration_failure', 'v2 unreachable'),
        migrationAttempt
      };
    }

    this.setAttemptStamp(scope);

    const legacyChats = this.readLegacyChats();
    const chats = this.toAggregates(scope, legacyChats);
    const totalChatCount = chats.length;

    try {
      const attemptCount = totalChatCount > 0
        ? await this.uploadAllChatsWithRetry(scope.accountKey, chats)
        : 0;

      const successMarker: PolarisMigrationSuccessMarker = {
        status: 'success',
        completedAt: Date.now(),
        version: 1,
        environment: getPolarisEnvId()
      };
      this.setSuccessMarker(scope, successMarker);

      const migrationAttempt: PolarisMigrationAttemptSuccessSummary = {
        status: 'success',
        totalChatCount,
        uploadedChatCount: totalChatCount,
        attemptCount
      };

      return {
        modeSelection: this.buildModeSelection('new', 'migration_success'),
        migrationAttempt
      };
    } catch (error) {
      const normalizedError = this.normalizeUploadError(error);
      const migrationAttempt: PolarisMigrationAttemptFailureSummary = {
        status: 'failed',
        totalChatCount,
        uploadedChatCount: 0,
        attemptCount: normalizedError.attemptCount,
        errorCategory: normalizedError.category,
        statusCode: normalizedError.statusCode,
        retryExhausted: normalizedError.retryExhausted,
        errorMessage: normalizedError.message
      };
      return {
        modeSelection: this.buildModeSelection('legacy', 'migration_failure', migrationAttempt.errorMessage),
        migrationAttempt
      };
    }
  }

  public getSuccessMarker(scope: PolarisMigrationScope): PolarisMigrationSuccessMarker | null {
    const statusMap = this.readMigrationStatusMap();
    const raw = statusMap[this.buildScopeKey(scope)] ?? null;
    if (!raw || raw.status !== 'success') {
      return null;
    }
    const currentEnv = getPolarisEnvId();
    const storedEnv = raw.environment as PolarisMigrationSuccessMarker['environment'] | undefined;
    if (!storedEnv || storedEnv !== currentEnv) {
      return null;
    }
    return raw as PolarisMigrationSuccessMarker;
  }

  public setSuccessMarker(scope: PolarisMigrationScope, marker: PolarisMigrationSuccessMarker): void {
    const statusMap = this.readMigrationStatusMap();
    statusMap[this.buildScopeKey(scope)] = marker;
    this.storageService.local.setItem(LocalStorageKeys.POLARIS_MIGRATION_STATUS, statusMap);
  }

  private readLegacyChats(): PolarisLegacyChatsMap {
    try {
      const raw = localStorage.getItem('copilot_chats');
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw) as PolarisLegacyChatsMap;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private toAggregates(scope: PolarisMigrationScope, legacyChats: PolarisLegacyChatsMap): PolarisChatAggregate[] {
    return Object.values(legacyChats).map(chat => this.toAggregate(scope, chat));
  }

  private toAggregate(scope: PolarisMigrationScope, chat: PolarisLegacyChat): PolarisChatAggregate {
    const createdTimestamp = this.getCreatedTimestamp(chat.messages, chat.lastUpdate);
    const lastModifiedTimestamp = this.normalizeTimestamp(chat.lastUpdate, createdTimestamp);
    const turns = this.toTurns(chat.id, chat.messages, chat);

    return {
      overview: {
        chatId: chat.id,
        accountKey: scope.accountKey,
        userId: scope.userId,
        title: chat.title || 'New Chat',
        parentChatId: null,
        createdAt: new Date(createdTimestamp).toISOString(),
        lastModified: new Date(lastModifiedTimestamp).toISOString(),
        lastMessageTime: new Date(lastModifiedTimestamp).toISOString(),
        isShared: false
      },
      turns,
      isThinking: false
    };
  }

  private toTurns(chatId: PolarisChatId, messages: PolarisLegacyChatMessage[], chat: PolarisLegacyChat): PolarisTurn[] {
    const assistantByInteraction = new Map<string, PolarisLegacyChatMessage>();
    for (const message of messages) {
      if (message.author === 'assistant' && message.interactionId && !assistantByInteraction.has(message.interactionId)) {
        assistantByInteraction.set(message.interactionId, message);
      }
    }

    const turns: PolarisTurn[] = [];
    for (let index = 0; index < messages.length; index += 1) {
      const userMessage = messages[index];
      if (userMessage.author !== 'user') {
        continue;
      }

      const assistantMessage = userMessage.interactionId
        ? assistantByInteraction.get(userMessage.interactionId)
        : this.findNextAssistant(messages, index + 1);

      const status: PolarisTurnStatus = assistantMessage?.error ? 'failed' : 'completed';
      const messagePair: PolarisMessagePair = {
        id: assistantMessage?.id ?? userMessage.id,
        chatId,
        userPrompt: userMessage.content,
        response: assistantMessage?.content ?? '',
        status,
        userTimestamp: this.normalizeTimestamp(userMessage.timestamp, Date.now()),
        systemTimestamp: assistantMessage?.timestamp ?? null,
        followUpQuestions: assistantMessage?.followUpQuestions ?? [],
        insights: assistantMessage?.insights ? [assistantMessage.insights] : null,
        creativesURLs: assistantMessage?.creativesURLs ?? [],
        graphs: this.legacyGraphsToDomain(assistantMessage?.graphs),
        polarisLambdaRequestId: null,
        interactionId: userMessage.interactionId ?? assistantMessage?.interactionId,
        error: assistantMessage?.error
      };

      turns.push({
        id: messagePair.interactionId ?? messagePair.id,
        chatId,
        status,
        origin: this.toOrigin(userMessage.origin ?? chat.origin),
        widgetName: userMessage.widgetName ?? chat.widgetName,
        widgetContext: this.toWidgetContext(
          userMessage.widgetContext ?? chat.widgetContext,
          userMessage.origin ?? chat.origin,
          userMessage.widgetName ?? chat.widgetName,
          chat.myBrand,
          chat.myCompetitors
        ),
        myBrand: chat.myBrand,
        myCompetitors: chat.myCompetitors,
        messagePair
      });
    }

    return turns;
  }

  private legacyGraphsToDomain(legacy?: graphSpec[]): PolarisGraph[] {
    if (!Array.isArray(legacy)) {
      return [];
    }
    const result: PolarisGraph[] = [];
    for (const item of legacy) {
      if (!item || typeof item !== 'object' || !('title' in item) || !('x' in item) || !('series' in item)) {
        continue;
      }
      const series: PolarisGraphSeries[] = Array.isArray(item.series)
        ? item.series.map(s => ({
            name: s?.name ?? '',
            y: Array.isArray(s?.y) ? s.y : [],
            unit: s?.unit ?? null
          }))
        : [];
      result.push({
        title: item.title ?? '',
        type: this.legacyGraphTypeToDomain(item.type),
        url: item.url ?? null,
        x: Array.isArray(item.x) ? item.x : [],
        series,
        yIsPercentage: item.y_is_percentage ?? false,
        xAxis: item.x_axis ?? '',
        yAxis: item.y_axis ?? ''
      });
    }
    return result;
  }

  private legacyGraphTypeToDomain(type: graphSpec['type'] | undefined): PolarisGraphType {
    switch (type) {
      case 'bar':
        return 'bar_chart';
      case 'line':
        return 'line_chart';
      case 'doughnut':
        return 'pie_chart';
      default:
        return 'bar_chart';
    }
  }

  private findNextAssistant(messages: PolarisLegacyChatMessage[], fromIndex: number): PolarisLegacyChatMessage | undefined {
    for (let index = fromIndex; index < messages.length; index += 1) {
      if (messages[index].author === 'assistant') {
        return messages[index];
      }
    }
    return undefined;
  }

  private toOrigin(origin: string | undefined): PolarisTurn['origin'] {
    return toPolarisOrigin(origin);
  }

  private getCreatedTimestamp(messages: PolarisLegacyChatMessage[], fallback: number): number {
    const timestamps = messages
      .map(message => this.normalizeTimestamp(message.timestamp, 0))
      .filter(timestamp => timestamp > 0);

    if (timestamps.length === 0) {
      return this.normalizeTimestamp(fallback, Date.now());
    }
    return Math.min(...timestamps);
  }

  private normalizeTimestamp(value: number | undefined, fallback: number): number {
    if (!value || Number.isNaN(value) || value <= 0) {
      return fallback;
    }
    return value;
  }

  private readMigrationStatusMap(): Record<string, PolarisMigrationSuccessMarker & { environment?: PolarisEnvId }> {
    return (this.storageService.local.getItem(LocalStorageKeys.POLARIS_MIGRATION_STATUS) ?? {}) as Record<
      string,
      PolarisMigrationSuccessMarker & { environment?: PolarisEnvId }
    >;
  }

  private readAttemptStampMap(): Record<string, { attemptedAt: number; environment: string }> {
    return this.storageService.local.getItem(LocalStorageKeys.POLARIS_MIGRATION_ATTEMPT_STAMP) ?? {};
  }

  private setAttemptStamp(scope: PolarisMigrationScope): void {
    const map = this.readAttemptStampMap();
    map[this.buildScopeKey(scope)] = {
      attemptedAt: Date.now(),
      environment: getPolarisEnvId()
    };
    this.storageService.local.setItem(LocalStorageKeys.POLARIS_MIGRATION_ATTEMPT_STAMP, map);
  }

  private buildScopeKey(scope: PolarisMigrationScope): string {
    return `${scope.accountKey}:${scope.userId}`;
  }

  private buildModeSelection(
    mode: PolarisRuntimeModeSelection['mode'],
    source: PolarisRuntimeModeSelection['source'],
    reason?: string
  ): PolarisRuntimeModeSelection {
    return {
      mode,
      source,
      selectedAt: Date.now(),
      reason
    };
  }

  private errorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return 'Migration failed';
  }

  private async uploadAllChatsWithRetry(accountKey: string, chats: PolarisChatAggregate[]): Promise<number> {
    let attempt = 0;
    let lastError: unknown = null;

    while (attempt < this.maxUploadAttempts) {
      attempt += 1;

      try {
        await firstValueFrom(this.polarisApiService.uploadChats(accountKey, chats));
        return attempt;
      } catch (error) {
        const normalizedError = this.normalizeUploadError(error, attempt);
        lastError = normalizedError;
        const canRetry = normalizedError.retryable && attempt < this.maxUploadAttempts;
        if (!canRetry) {
          throw normalizedError;
        }
        await this.waitBeforeRetry(attempt);
      }
    }

    throw this.normalizeUploadError(lastError, attempt);
  }

  private async waitBeforeRetry(attempt: number): Promise<void> {
    const delayMs = this.retryBaseDelayMs * attempt;
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }

  private normalizeUploadError(
    error: unknown,
    attemptCount = this.maxUploadAttempts
  ): PolarisMigrationUploadError {
    if (this.isPolarisMigrationUploadError(error)) {
      return error;
    }

    const category = this.classifyErrorCategory(error);
    const statusCode = this.extractStatusCode(error);
    const message = this.errorMessage(error);

    return {
      attemptCount,
      category,
      statusCode,
      retryable: category === 'network' || category === 'server',
      retryExhausted: attemptCount >= this.maxUploadAttempts,
      message
    };
  }

  private classifyErrorCategory(error: unknown): PolarisMigrationErrorCategory {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'network';
      }
      if (error.status === 403) {
        return 'forbidden';
      }
      if (error.status >= 500) {
        return 'server';
      }
      if (error.status >= 400) {
        return 'validation';
      }
      return 'unknown';
    }
    return 'unknown';
  }

  private extractStatusCode(error: unknown): number | undefined {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }
    return undefined;
  }

  private isPolarisMigrationUploadError(error: unknown): error is PolarisMigrationUploadError {
    if (!error || typeof error !== 'object') {
      return false;
    }
    const candidate = error as Partial<PolarisMigrationUploadError>;
    return typeof candidate.message === 'string'
      && typeof candidate.attemptCount === 'number'
      && typeof candidate.retryable === 'boolean'
      && typeof candidate.retryExhausted === 'boolean'
      && typeof candidate.category === 'string';
  }

  private toWidgetContext(
    rawContext: unknown,
    origin: string | undefined,
    widgetName: string | undefined,
    myBrand: string | undefined,
    myCompetitors: string[] | undefined
  ): PolarisTurn['widgetContext'] {
    if (!rawContext || typeof rawContext !== 'object') {
      if (!origin && !widgetName && !myBrand && !myCompetitors?.length) {
        return undefined;
      }
      return {
        origin: this.toOrigin(origin),
        name: widgetName,
        myBrand,
        myCompetitors
      };
    }

    const context = rawContext as Record<string, unknown>;
    const metrics = Array.isArray(context['metrics']) ? context['metrics'].filter(item => typeof item === 'string') : undefined;
    const countries = Array.isArray(context['countries']) ? context['countries'].filter(item => typeof item === 'string') : undefined;
    const contextCompetitors = Array.isArray(context['myCompetitors'])
      ? context['myCompetitors'].filter(item => typeof item === 'string')
      : myCompetitors;

    return {
      origin: this.toOrigin((context['origin'] as string | undefined) ?? origin),
      name: typeof context['name'] === 'string' ? context['name'] : widgetName,
      metrics,
      myBrand: typeof context['myBrand'] === 'string' ? context['myBrand'] : myBrand,
      myCompetitors: contextCompetitors,
      dateRange: this.toWidgetDateRange(context['dateRange']),
      countries,
      device: typeof context['device'] === 'string' ? context['device'] : undefined
    };
  }

  private toWidgetDateRange(input: unknown): {from: string; to: string} | undefined {
    if (!input || typeof input !== 'object') {
      return undefined;
    }
    const value = input as {from?: unknown; to?: unknown};
    if (typeof value.from !== 'string' || typeof value.to !== 'string') {
      return undefined;
    }
    return {
      from: value.from,
      to: value.to
    };
  }
}
